import React from 'react';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import ThreeSixtyOutlinedIcon from '@mui/icons-material/ThreeSixtyOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import CoPresentOutlinedIcon from '@mui/icons-material/CoPresentOutlined';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ProjectFilesPanel from './ProjectFilesPanel';
import { EventPosterCard } from './EventPosterCard';
import VirtualTourDialog from './VirtualTourDialog';
import { CircularProgress } from '@mui/material';
import {
  PaginatedFileList,
  isPreviewableVideo,
  isPdfFile,
  type PaginatedFileListItem,
} from './fileVisuals';
import {
  logProjectResourceEdmPreviewed,
  logProjectResourceEdmDownloaded,
  logProjectResourceEbookPreviewed,
  logProjectResourceEbookDownloaded,
} from '../services/analytics';
import {
  projectEssentialApi,
  type EdmTabItem,
  type ProjectFileGroup,
  type ProjectMediaItem,
  type ProjectResourceLink,
  type ProjectActivityItem,
} from '../services/api';

export type ProjectResourceTabId =
  | 'highlights'
  | 'edm'
  | 'seminars'
  | 'media'
  | 'files'
  | 'agentAccess'
  | 'saleskit';

interface ProjectResourcesDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName?: string | null;
  initialTab?: ProjectResourceTabId;
  /**
   * Upcoming GLS cards pass the ProjectLandSale row id as `projectId`
   * with `resourceSource="gls"` so EDM / eBook tabs resolve via
   * ProjectLandSale.ProjectId → EDMProject instead of treating the id
   * as a Project.Id.
   */
  resourceSource?: 'project' | 'gls';
  /** Upcoming GLS surfaces hide Saleskit and Agent Access Folder tabs. */
  isUpcomingGls?: boolean;
}

interface TabDef {
  id: ProjectResourceTabId;
  label: string;
  icon: React.ReactNode;
  emptyHint: string;
}

const TAB_DEFS: TabDef[] = [
  {
    id: 'edm',
    label: 'EDM & VVIP Passes',
    icon: <ConfirmationNumberOutlinedIcon fontSize="small" />,
    emptyHint: 'There are currently no related EDMs or VVIP passes for this project.',
  },
  {
    id: 'highlights',
    label: 'eBooks',
    icon: <MenuBookIcon fontSize="small" />,
    emptyHint: 'There are currently no related eBooks for this project.',
  },
  {
    id: 'seminars',
    label: 'Seminars & Events',
    icon: <EventOutlinedIcon fontSize="small" />,
    emptyHint: 'There are currently no related seminars or events for this project.',
  },
  {
    id: 'media',
    label: 'Media',
    icon: <PermMediaOutlinedIcon fontSize="small" />,
    emptyHint: 'There are currently no related media for this project.',
  },
  {
    id: 'files',
    label: 'Files',
    icon: <FolderOpenOutlinedIcon fontSize="small" />,
    emptyHint: 'There are currently no related files for this project.',
  },
  {
    id: 'agentAccess',
    label: 'Agent Access Folder',
    icon: <FolderSharedOutlinedIcon fontSize="small" />,
    emptyHint: 'There are currently no shared links for this project.',
  },
  {
    id: 'saleskit',
    label: 'Saleskit',
    icon: <CoPresentOutlinedIcon fontSize="small" />,
    emptyHint: 'There are currently no saleskit materials for this project.',
  },
];

interface FilesState {
  data: { groups: ProjectFileGroup[]; total: number } | null;
  loading: boolean;
  error: string | null;
}

interface EbooksState {
  items: EdmTabItem[];
  loading: boolean;
  error: string | null;
}

interface EdmsState {
  items: EdmTabItem[];
  loading: boolean;
  error: string | null;
}

interface MediaState {
  items: ProjectMediaItem[];
  loading: boolean;
  error: string | null;
}

type MediaSubTabId = 'gallery' | 'videos' | 'virtualTour';

const MEDIA_SUB_TABS: { id: MediaSubTabId; label: string; icon: React.ReactNode }[] = [
  { id: 'gallery', label: 'Gallery', icon: <PhotoLibraryOutlinedIcon fontSize="small" /> },
  { id: 'videos', label: 'Videos', icon: <SmartDisplayOutlinedIcon fontSize="small" /> },
  { id: 'virtualTour', label: 'Virtual tour', icon: <ThreeSixtyOutlinedIcon fontSize="small" /> },
];

interface SeminarsState {
  items: ProjectActivityItem[];
  loading: boolean;
  error: string | null;
}

interface ResourceLinksState {
  items: ProjectResourceLink[];
  loading: boolean;
  error: string | null;
}

interface TabVisibilityInput {
  filesState: FilesState;
  saleskitState: FilesState;
  ebooksState: EbooksState;
  edmsState: EdmsState;
  mediaState: MediaState;
  seminarsState: SeminarsState;
  resourceLinksState: ResourceLinksState;
}

function getMediaItemCount(items: ProjectMediaItem[]): number {
  return items.filter((item) => {
    const type = normalizeMediaType(item.type);
    return type === 'image' || type === 'video' || type === 'ivt';
  }).length;
}

/** `null` while loading or on error — tab stays visible until resolved. */
function getTabItemCount(tabId: ProjectResourceTabId, input: TabVisibilityInput): number | null {
  switch (tabId) {
    case 'files':
      if (input.filesState.loading || input.filesState.error) return null;
      return input.filesState.data?.total ?? 0;
    case 'highlights':
      if (input.ebooksState.loading || input.ebooksState.error) return null;
      return input.ebooksState.items.length;
    case 'edm':
      if (input.edmsState.loading || input.edmsState.error) return null;
      return input.edmsState.items.length;
    case 'media':
      if (input.mediaState.loading || input.mediaState.error) return null;
      return getMediaItemCount(input.mediaState.items);
    case 'seminars':
      if (input.seminarsState.loading || input.seminarsState.error) return null;
      return input.seminarsState.items.length;
    case 'agentAccess':
      if (input.resourceLinksState.loading || input.resourceLinksState.error) return null;
      return input.resourceLinksState.items.length;
    case 'saleskit':
      if (input.saleskitState.loading || input.saleskitState.error) return null;
      return input.saleskitState.data?.total ?? 0;
    default:
      return null;
  }
}

function isTabVisible(
  tabId: ProjectResourceTabId,
  input: TabVisibilityInput,
  resourceSource: 'project' | 'gls',
  isUpcomingGls: boolean,
): boolean {
  if (
    (resourceSource === 'gls' || isUpcomingGls) &&
    (tabId === 'agentAccess' || tabId === 'saleskit')
  ) {
    return false;
  }
  if (tabId === 'agentAccess' || tabId === 'saleskit') return true;
  const count = getTabItemCount(tabId, input);
  if (count === null) return true;
  return count > 0;
}

function getVisibleTabs(
  input: TabVisibilityInput,
  resourceSource: 'project' | 'gls',
  isUpcomingGls: boolean,
): TabDef[] {
  return TAB_DEFS.filter((t) => isTabVisible(t.id, input, resourceSource, isUpcomingGls));
}

function AllResourcesEmptyState() {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 4, sm: 6 }, textAlign: 'center' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
        No resources available
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', maxWidth: 360 }}>
        There are currently no EDMs, eBooks, events, media, files, or shared links for this project.
      </Typography>
    </Stack>
  );
}

// Format an ISO timestamp as e.g. "08 May 2026". Returns "" when the input
// is null / invalid so the caller can skip rendering the caption entirely.
function formatCreatedDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function normalizeMediaType(type: string | null | undefined): string {
  return (type || '').trim().toLowerCase();
}

const MEDIA_PREVIEW_IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;
const MEDIA_PREVIEW_VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogg|ogv)$/i;

function defaultMediaExt(type: string): string {
  if (type === 'image') return 'jpg';
  if (type === 'video') return 'mp4';
  return 'html';
}

function hasPreviewableMediaExt(name: string, type: string): boolean {
  if (type === 'image') return MEDIA_PREVIEW_IMAGE_EXT.test(name);
  if (type === 'video') return MEDIA_PREVIEW_VIDEO_EXT.test(name);
  return /\.[a-z0-9]{1,8}$/i.test(name);
}

/**
 * Derive a filename the file-visuals preview gate can recognise.
 * Portal media URLs often end in handler paths (`Download`, `GetImage`)
 * with the real extension only in a query param — or nowhere at all.
 * In those cases we synthesise `media-<n>.jpg|mp4` from `Type` so
 * gallery/video rows still open `FilePreviewDialog`.
 */
function filenameFromMediaUrl(
  url: string | null | undefined,
  mediaNumber: number,
  type: string,
): string {
  const fallback = `media-${mediaNumber}.${defaultMediaExt(type)}`;
  if (!url) return fallback;

  try {
    const u = new URL(url);
    for (const key of ['fileName', 'filename', 'file']) {
      const param = u.searchParams.get(key);
      if (param && param.trim()) {
        const decoded = decodeURIComponent(param.trim());
        if (hasPreviewableMediaExt(decoded, type)) return decoded;
      }
    }
    const base = u.pathname.split('/').filter(Boolean).pop();
    if (base) {
      const decoded = decodeURIComponent(base);
      if (hasPreviewableMediaExt(decoded, type)) return decoded;
    }
  } catch {
    const base = url.split(/[\\/?#]/).filter(Boolean).pop();
    if (base && hasPreviewableMediaExt(base, type)) return base;
  }

  return fallback;
}

/** Prefer the playable/viewable asset URL for inline preview. */
function mediaPreviewUrl(item: ProjectMediaItem, type: string): string | undefined {
  if (type === 'image') {
    return item.url || item.imageLarge || item.imageMedium || item.imageSmall || undefined;
  }
  return item.url || undefined;
}

function isStreamPageUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    return (
      host === 'youtube.com'
      || host === 'm.youtube.com'
      || host === 'youtu.be'
      || host === 'vimeo.com'
      || host === 'player.vimeo.com'
    );
  } catch {
    return false;
  }
}

function mapProjectMediaToListItem(
  item: ProjectMediaItem,
  options?: {
    onOpenVirtualTour?: (tour: { title: string; url: string }) => void;
  },
): PaginatedFileListItem {
  const type = normalizeMediaType(item.type);
  const url = mediaPreviewUrl(item, type);
  const filename = filenameFromMediaUrl(url ?? item.url, item.mediaNumber, type);
  const previewImage =
    type === 'image'
      ? (item.url
        || item.imageMedium
        || item.imageLarge
        || item.imageSmall
        || item.videoThumbnail
        || null)
      : (item.imageMedium
        || item.imageLarge
        || item.imageSmall
        || item.videoThumbnail
        || null);
  const displayName = (item.description || '').trim() || undefined;

  if (type === 'ivt') {
    const externalLink = (item.externalLink || '').trim();
    const linkToCopy = externalLink || item.url || '';
    const title = displayName || 'Virtual tour';
    return {
      id: String(item.mediaNumber),
      filename: filename || `virtual-tour-${item.mediaNumber}.html`,
      displayName: title,
      subtitle: 'Virtual tour',
      url: linkToCopy || undefined,
      previewImage,
      disablePreview: true,
      actionMode: 'copy',
      copyText: linkToCopy,
      typeLabel: 'Click to Open',
      onClick: () => {
        const href = linkToCopy.trim();
        if (!href) return;
        options?.onOpenVirtualTour?.({ title, url: href });
      },
    };
  }

  const base: PaginatedFileListItem = {
    id: String(item.mediaNumber),
    filename,
    displayName,
    url,
    previewImage,
    sizeBytes: item.size ?? undefined,
    // CDN media (Singmap etc.) needs a blob fetch so Download saves the
    // file instead of navigating to the asset in a new tab.
    forceBlobDownload: true,
    // `ProjectMedia.UpdatedAt` is a sync timestamp, not an upload date, so
    // it is deliberately not surfaced as "Uploaded ..." on the card.
  };

  if (type === 'video' && url && !isPreviewableVideo(filename) && isStreamPageUrl(url)) {
    return {
      ...base,
      forceBlobDownload: false,
      disablePreview: true,
      onClick: () => window.open(url, '_blank', 'noopener,noreferrer'),
    };
  }

  return base;
}

function MediaTabEmptyState({
  icon,
  label,
  emptyHint,
}: {
  icon: React.ReactNode;
  label: string;
  emptyHint: string;
}) {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'var(--color-accent-soft)',
          color: 'var(--color-accent-strong)',
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', maxWidth: 360 }}>
        {emptyHint}
      </Typography>
    </Stack>
  );
}

function ProjectMediaPanel({
  state,
  tabIcon,
  tabLabel,
  emptyHint,
}: {
  state: MediaState;
  tabIcon: React.ReactNode;
  tabLabel: string;
  emptyHint: string;
}) {
  const [subTab, setSubTab] = React.useState<MediaSubTabId>('gallery');
  const [virtualTour, setVirtualTour] = React.useState<{ title: string; url: string } | null>(null);

  if (state.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
          {state.error}
        </Typography>
      </Stack>
    );
  }

  const galleryItems = state.items.filter((i) => normalizeMediaType(i.type) === 'image');
  const videoItems = state.items.filter((i) => normalizeMediaType(i.type) === 'video');
  const ivtItems = state.items.filter((i) => normalizeMediaType(i.type) === 'ivt');
  const totalCount = galleryItems.length + videoItems.length + ivtItems.length;

  const subTabCounts: Record<MediaSubTabId, number> = {
    gallery: galleryItems.length,
    videos: videoItems.length,
    virtualTour: ivtItems.length,
  };

  const visibleSubTabs = MEDIA_SUB_TABS.filter((t) => subTabCounts[t.id] > 0);

  if (totalCount === 0) {
    return <MediaTabEmptyState icon={tabIcon} label={tabLabel} emptyHint={emptyHint} />;
  }

  const subTabItems: Record<MediaSubTabId, ProjectMediaItem[]> = {
    gallery: galleryItems,
    videos: videoItems,
    virtualTour: ivtItems,
  };

  const emptyLabels: Record<MediaSubTabId, string> = {
    gallery: 'No gallery images for this project.',
    videos: 'No videos for this project.',
    virtualTour: 'No virtual tours for this project.',
  };

  const activeSubTab =
    visibleSubTabs.some((t) => t.id === subTab) ? subTab : visibleSubTabs[0].id;
  const activeItems = subTabItems[activeSubTab].map((item) =>
    mapProjectMediaToListItem(item, {
      onOpenVirtualTour: (tour) => setVirtualTour(tour),
    }),
  );

  return (
    <Stack spacing={2} sx={{ p: '2px' }}>
      {visibleSubTabs.length > 1 && (
        <Tabs
          value={activeSubTab}
          onChange={(_e, value: MediaSubTabId) => setSubTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            borderBottom: '1px solid var(--color-border)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.78rem', sm: '0.85rem' },
              color: 'var(--color-text-muted)',
              minHeight: 40,
              gap: 0.5,
              '&.Mui-selected': { color: 'var(--color-accent-strong)' },
            },
            '& .MuiTabs-indicator': { bgcolor: 'var(--color-accent)' },
          }}
        >
          {visibleSubTabs.map((t) => (
            <Tab
              key={t.id}
              value={t.id}
              icon={t.icon as React.ReactElement}
              iconPosition="start"
              label={`${t.label} (${subTabCounts[t.id]})`}
            />
          ))}
        </Tabs>
      )}
      <PaginatedFileList items={activeItems} emptyLabel={emptyLabels[activeSubTab]} />
      <VirtualTourDialog
        open={Boolean(virtualTour)}
        onClose={() => setVirtualTour(null)}
        title={virtualTour?.title || 'Virtual tour'}
        url={virtualTour?.url || null}
      />
    </Stack>
  );
}

function ResourceTabPanel({
  active,
  tab,
  projectId,
  filesState,
  saleskitState,
  ebooksState,
  edmsState,
  mediaState,
  seminarsState,
  resourceLinksState,
}: {
  active: boolean;
  tab: TabDef;
  projectId: string;
  filesState: FilesState;
  saleskitState: FilesState;
  ebooksState: EbooksState;
  edmsState: EdmsState;
  mediaState: MediaState;
  seminarsState: SeminarsState;
  resourceLinksState: ResourceLinksState;
}) {
  const [seminarsPage, setSeminarsPage] = React.useState(1);

  React.useEffect(() => {
    if (active) {
      setSeminarsPage(1);
    }
  }, [projectId, active]);

  if (!active) return null;

  const seminarsPageSize = 6;
  const seminarsPageCount = Math.ceil(seminarsState.items.length / seminarsPageSize);
  const safeSeminarsPage = Math.min(seminarsPage, seminarsPageCount || 1);
  const slicedSeminars = seminarsState.items.slice(
    (safeSeminarsPage - 1) * seminarsPageSize,
    safeSeminarsPage * seminarsPageSize,
  );

  return (
    <Box
      role="tabpanel"
      sx={{
        py: { xs: 2, sm: 2.5 },
        minHeight: { xs: 220, sm: 280 },
      }}
    >
      {tab.id === 'files' ? (
        <ProjectFilesPanel
          projectId={projectId}
          data={filesState.data}
          loading={filesState.loading}
          error={filesState.error}
          enableBulkSelection={false}
        />
      ) : tab.id === 'saleskit' ? (
        <ProjectFilesPanel
          projectId={projectId}
          data={saleskitState.data}
          loading={saleskitState.loading}
          error={saleskitState.error}
          enableBulkSelection={false}
        />
      ) : tab.id === 'highlights' ? (
        // Live launcher eBooks for this project, sourced from the
        // ProjectLauncherProject join table. Falls back to the same
        // empty-state hint as the other not-yet-wired tabs when no
        // rows match — keeps the dialog feeling consistent.
        ebooksState.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : ebooksState.error ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
              {ebooksState.error}
            </Typography>
          </Stack>
        ) : ebooksState.items.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'var(--color-accent-soft)',
                color: 'var(--color-accent-strong)',
              }}
            >
              {tab.icon}
            </Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
            >
              {tab.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'var(--color-text-muted)', maxWidth: 360 }}
            >
              {tab.emptyHint}
            </Typography>
          </Stack>
        ) : (() => {
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

          const mappedEbooks = ebooksState.items.map((item) => ({
            id: item.id,
            filename: `${item.title || item.id}.pdf`,
            displayName: item.title,
            subtitle: 'Client Engagement eBook',
            url: item.url && item.url !== '#' ? item.url : item.urlPublic,
            urlPublic: item.url && item.url !== '#' ? item.urlPublic : null,
            previewImage: item.cover,
            uploadedAt: item.createdAt,
            onPreview: () => logProjectResourceEbookPreviewed(item.id),
            onDownloadClick: () => logProjectResourceEbookDownloaded(item.id),
            forceBlobDownload: true,
            shareAsset: {
              id: item.id,
              kind: 'ebook' as const,
              title: item.title || item.id,
              projectId,
            },
          }));

          const recentEbooks = mappedEbooks.filter((item) => {
            if (!item.uploadedAt) return false;
            const d = new Date(item.uploadedAt);
            return !isNaN(d.getTime()) && d >= threeMonthsAgo;
          });

          const pastEbooks = mappedEbooks.filter((item) => {
            if (!item.uploadedAt) return true;
            const d = new Date(item.uploadedAt);
            return isNaN(d.getTime()) || d < threeMonthsAgo;
          });

          return (
            <Stack spacing={2.5} sx={{ p: '2px' }}>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    color: 'var(--color-accent)',
                    px: 0.5,
                    mb: 1.5,
                    fontSize: '1.05rem',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  Recent Uploads (Last 3 months)
                </Typography>
                {recentEbooks.length > 0 ? (
                  <PaginatedFileList
                    items={recentEbooks}
                    emptyLabel="No recent eBooks linked to this project."
                  />
                ) : (
                  <Box
                    sx={{
                      minHeight: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px dashed var(--color-border-strong)',
                      borderRadius: 2,
                      bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                      p: 3,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'var(--color-text-muted)',
                        fontStyle: 'italic',
                        textAlign: 'center',
                      }}
                    >
                      No eBooks uploaded within the last 3 months
                    </Typography>
                  </Box>
                )}
              </Box>

              {pastEbooks.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      color: 'var(--color-text-muted)',
                      px: 0.5,
                      mb: 1.5,
                      fontSize: '1.05rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Past Uploads
                  </Typography>
                  <PaginatedFileList
                    items={pastEbooks}
                    emptyLabel="No past eBooks linked to this project."
                  />
                </Box>
              )}
            </Stack>
          );
        })()
      ) : tab.id === 'edm' ? (
        // Live EDMs / VVIP passes for this project, sourced from the
        // EDMProject join table. Same loading/error/empty pattern as
        // the highlights branch above so the dialog feels consistent.
        edmsState.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : edmsState.error ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
              {edmsState.error}
            </Typography>
          </Stack>
        ) : edmsState.items.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'var(--color-accent-soft)',
                color: 'var(--color-accent-strong)',
              }}
            >
              {tab.icon}
            </Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
            >
              {tab.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'var(--color-text-muted)', maxWidth: 360 }}
            >
              {tab.emptyHint}
            </Typography>
          </Stack>
        ) : (() => {
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

          const mappedEdms = edmsState.items.map((item) => ({
            id: item.id,
            filename: item.filename,
            displayName: item.title,
            // EPass rows render a "VVIP Pass" subtitle so users can
            // distinguish them from regular Project EDMs at a glance.
            subtitle: item.category === 'EPass' ? 'VVIP Pass' : 'Project EDM',
            // With-agent URL powers preview (via GetEDM temp asset) and
            // is the default download target. Public URL is download-only.
            url: item.url && item.url !== '#' ? item.url : item.urlPublic,
            urlPublic: item.url && item.url !== '#' ? item.urlPublic : null,
            // Use the curated `EDM.Thumbnail` (already absolutised
            // by the backend) as the row preview rather than
            // rasterising the first page of the PDF — same image
            // the EDM catalogue grid uses, much cheaper to fetch.
            previewImage: item.cover,
            uploadedAt: item.createdAt,
            onPreview: () => logProjectResourceEdmPreviewed(item.id),
            onDownloadClick: () => logProjectResourceEdmDownloaded(item.id),
            shareAsset: {
              id: item.id,
              kind: 'edm' as const,
              title: item.title || item.id,
              projectId,
            },
          }));

          const recentEdms = mappedEdms.filter((item) => {
            if (!item.uploadedAt) return false;
            const d = new Date(item.uploadedAt);
            return !isNaN(d.getTime()) && d >= threeMonthsAgo;
          });

          const pastEdms = mappedEdms.filter((item) => {
            if (!item.uploadedAt) return true;
            const d = new Date(item.uploadedAt);
            return isNaN(d.getTime()) || d < threeMonthsAgo;
          });

          return (
            <Stack spacing={2.5} sx={{ p: '2px' }}>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    color: 'var(--color-accent)',
                    px: 0.5,
                    mb: 1.5,
                    fontSize: '1.05rem',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  Recent Uploads (Last 3 months)
                </Typography>
                {recentEdms.length > 0 ? (
                  <PaginatedFileList
                    items={recentEdms}
                    emptyLabel="No recent EDMs linked to this project."
                  />
                ) : (
                  <Box
                    sx={{
                      minHeight: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px dashed var(--color-border-strong)',
                      borderRadius: 2,
                      bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                      p: 3,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'var(--color-text-muted)',
                        fontStyle: 'italic',
                        textAlign: 'center',
                      }}
                    >
                      No EDMs uploaded within the last 3 months
                    </Typography>
                  </Box>
                )}
              </Box>

              {pastEdms.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      color: 'var(--color-text-muted)',
                      px: 0.5,
                      mb: 1.5,
                      fontSize: '1.05rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Past Uploads
                  </Typography>
                  <PaginatedFileList
                    items={pastEdms}
                    emptyLabel="No past EDMs linked to this project."
                  />
                </Box>
              )}
            </Stack>
          );
        })()
      ) : tab.id === 'seminars' ? (
        seminarsState.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : seminarsState.error ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
              {seminarsState.error}
            </Typography>
          </Stack>
        ) : seminarsState.items.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'var(--color-accent-soft)',
                color: 'var(--color-accent-strong)',
              }}
            >
              {tab.icon}
            </Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
            >
              {tab.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'var(--color-text-muted)', maxWidth: 360 }}
            >
              {tab.emptyHint}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={3.5}>
            <Grid container spacing={2.5}>
              {slicedSeminars.map((event) => (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <EventPosterCard event={event} />
                </Grid>
              ))}
            </Grid>

            {seminarsPageCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Pagination
                  count={seminarsPageCount}
                  page={safeSeminarsPage}
                  onChange={(_e, p) => setSeminarsPage(p)}
                  color="primary"
                  shape="rounded"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'var(--color-text-strong)',
                      borderColor: 'var(--color-border-strong)',
                      fontWeight: 600,
                      '&.Mui-selected': {
                        bgcolor: 'var(--color-accent)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Stack>
        )
      ) : tab.id === 'media' ? (
        <ProjectMediaPanel
          state={mediaState}
          tabIcon={tab.icon}
          tabLabel={tab.label}
          emptyHint={tab.emptyHint}
        />
      ) : tab.id === 'agentAccess' ? (
        // Read-only curated links for the project. Write actions live on the
        // super-picker admin surface — this dialog is for browsing only.
        resourceLinksState.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : resourceLinksState.error ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
              {resourceLinksState.error}
            </Typography>
          </Stack>
        ) : resourceLinksState.items.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'var(--color-accent-soft)',
                color: 'var(--color-accent-strong)',
              }}
            >
              {tab.icon}
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
              {tab.label}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', maxWidth: 360 }}>
              {tab.emptyHint}
            </Typography>
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: { xs: 1, sm: 1.25 },
              p: '2px', // Prevent parent clipping of hover border shadows
            }}
          >
            {resourceLinksState.items.map((link) => (
              <Box
                key={link.id}
                component="a"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.25, sm: 2 },
                  p: { xs: 1.25, sm: 1.5 },
                  borderRadius: 2,
                  border: '1px solid var(--color-border-strong)',
                  bgcolor: 'var(--color-surface)',
                  color: 'inherit',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                  '&:hover': {
                    borderColor: 'var(--color-accent)',
                    boxShadow: '0 0 0 1px var(--color-accent)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'var(--color-accent-soft)',
                    color: 'var(--color-accent-strong)',
                    flexShrink: 0,
                  }}
                >
                  <LinkIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: 'var(--color-text-strong)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={link.label || link.url}
                  >
                    {link.label || link.url}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'var(--color-text-muted)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={link.url}
                  >
                    {link.url}
                  </Typography>
                  {formatCreatedDate(link.createdAt) && (
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                      Added {formatCreatedDate(link.createdAt)}
                    </Typography>
                  )}
                </Box>
                <OpenInNewIcon fontSize="small" sx={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        )
      ) : (
        <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 3, sm: 5 }, textAlign: 'center' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'var(--color-accent-soft)',
              color: 'var(--color-accent-strong)',
            }}
          >
            {tab.icon}
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
          >
            {tab.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'var(--color-text-muted)', maxWidth: 360 }}
          >
            {tab.emptyHint}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

export default function ProjectResourcesDialog({
  open,
  onClose,
  projectId,
  projectName,
  initialTab = 'edm',
  resourceSource = 'project',
  isUpcomingGls = false,
}: ProjectResourcesDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabId, setTabId] = React.useState<ProjectResourceTabId>(initialTab);
  // On mobile the dialog drills down: `null` shows the menu list, a tab id
  // shows that tab's content with a back arrow. Desktop always renders tabs.
  const [mobileSelected, setMobileSelected] = React.useState<ProjectResourceTabId | null>(null);
  const [filesState, setFilesState] = React.useState<FilesState>({
    data: null,
    loading: false,
    error: null,
  });
  const [saleskitState, setSaleskitState] = React.useState<FilesState>({
    data: null,
    loading: false,
    error: null,
  });
  const [ebooksState, setEbooksState] = React.useState<EbooksState>({
    items: [],
    loading: false,
    error: null,
  });
  const [edmsState, setEdmsState] = React.useState<EdmsState>({
    items: [],
    loading: false,
    error: null,
  });
  const [mediaState, setMediaState] = React.useState<MediaState>({
    items: [],
    loading: false,
    error: null,
  });
  const [seminarsState, setSeminarsState] = React.useState<SeminarsState>({
    items: [],
    loading: false,
    error: null,
  });
  const [resourceLinksState, setResourceLinksState] = React.useState<ResourceLinksState>({
    items: [],
    loading: false,
    error: null,
  });
  // Fetch the files list once the dialog opens so the count is available for
  // the tab label / mobile menu entry without waiting for the user to navigate.
  React.useEffect(() => {
    if (!open) return;
    let mounted = true;
    setFilesState({ data: null, loading: true, error: null });
    setSaleskitState({ data: null, loading: true, error: null });
    projectEssentialApi
      .getProjectFiles(projectId)
      .then((res) => {
        const saleskitGroups = res.groups.filter(
          (group) => group.category.trim().toUpperCase() === 'STS',
        );
        const saleskitTotal = saleskitGroups.reduce((sum, group) => sum + group.items.length, 0);

        // "Files" is PDF-only and excludes Saleskit / Training Slides.
        const pdfGroups = res.groups
          .filter((group) => group.category.trim().toUpperCase() !== 'STS')
          .map((g) => ({
            ...g,
            items: g.items.filter((f) => {
              const name = (f.filename || f.displayName || '').trim();
              return name ? isPdfFile(name) : false;
            }),
          }))
          .filter((g) => g.items.length > 0);

        const pdfTotal = pdfGroups.reduce((sum, g) => sum + g.items.length, 0);

        if (mounted) {
          setFilesState({ data: { groups: pdfGroups, total: pdfTotal }, loading: false, error: null });
          setSaleskitState({
            data: { groups: saleskitGroups, total: saleskitTotal },
            loading: false,
            error: null,
          });
        }
      })
      .catch((err) => {
        if (mounted) {
          const error = err?.message || 'Failed to load project files';
          setFilesState({
            data: null,
            loading: false,
            error,
          });
          setSaleskitState({ data: null, loading: false, error });
        }
      });
    return () => {
      mounted = false;
    };
  }, [open, projectId]);

  React.useEffect(() => {
    if (!open) return;
    let mounted = true;
    setEbooksState({ items: [], loading: true, error: null });
    const scope =
      resourceSource === 'gls'
        ? { landSaleId: projectId }
        : { projectId };
    projectEssentialApi
      .getEdmsByProject(scope, undefined, true)
      .then((res) => {
        if (mounted) setEbooksState({ items: res.items, loading: false, error: null });
      })
      .catch((err) => {
        if (mounted) setEbooksState({ items: [], loading: false, error: err?.message || 'Failed to load eBooks for this project' });
      });
    return () => { mounted = false; };
  }, [open, projectId, resourceSource]);

  React.useEffect(() => {
    if (!open) return;
    let mounted = true;
    setEdmsState({ items: [], loading: true, error: null });
    const scope =
      resourceSource === 'gls'
        ? { landSaleId: projectId }
        : { projectId };
    projectEssentialApi
      .getEdmsByProject(scope, undefined, false)
      .then((res) => {
        if (mounted) setEdmsState({ items: res.items, loading: false, error: null });
      })
      .catch((err) => {
        if (mounted) setEdmsState({ items: [], loading: false, error: err?.message || 'Failed to load EDMs for this project' });
      });
    return () => { mounted = false; };
  }, [open, projectId, resourceSource]);

  React.useEffect(() => {
    if (!open) return;
    let mounted = true;
    setMediaState({ items: [], loading: true, error: null });
    projectEssentialApi
      .getProjectMedia(projectId)
      .then((res) => {
        if (mounted) setMediaState({ items: res.items, loading: false, error: null });
      })
      .catch((err) => {
        if (mounted) setMediaState({ items: [], loading: false, error: err?.message || 'Failed to load media for this project' });
      });
    return () => { mounted = false; };
  }, [open, projectId]);

  React.useEffect(() => {
    if (!open) return;
    let mounted = true;
    setSeminarsState({ items: [], loading: true, error: null });
    projectEssentialApi
      .getProjectActivitiesByProject(projectId)
      .then((res) => {
        if (mounted) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const filteredAndSorted = (res.items || [])
            .filter((item) => {
              if (!item.start) return false;
              const dateVal = item.end ? new Date(item.end) : new Date(item.start);
              return dateVal >= today;
            })
            .sort((a, b) => {
              const dateA = a.start ? new Date(a.start).getTime() : 0;
              const dateB = b.start ? new Date(b.start).getTime() : 0;
              return dateA - dateB;
            });

          setSeminarsState({ items: filteredAndSorted, loading: false, error: null });
        }
      })
      .catch((err) => {
        if (mounted) setSeminarsState({ items: [], loading: false, error: err?.message || 'Failed to load seminars or events for this project' });
      });
    return () => { mounted = false; };
  }, [open, projectId]);

  // Load the curated Agent Access Folder links (read-only from this dialog).
  React.useEffect(() => {
    if (!open || resourceSource === 'gls' || isUpcomingGls) {
      if (!open) {
        setResourceLinksState({ items: [], loading: false, error: null });
      }
      return;
    }
    let mounted = true;
    setResourceLinksState({ items: [], loading: true, error: null });
    projectEssentialApi
      .getProjectResourceLinks(projectId)
      .then((res) => {
        if (mounted) setResourceLinksState({ items: res.items, loading: false, error: null });
      })
      .catch((err) => {
        if (mounted) setResourceLinksState({ items: [], loading: false, error: err?.message || 'Failed to load shared links for this project' });
      });
    return () => { mounted = false; };
  }, [open, projectId, resourceSource, isUpcomingGls]);

  const tabVisibilityInput: TabVisibilityInput = {
    filesState,
    saleskitState,
    ebooksState,
    edmsState,
    mediaState,
    seminarsState,
    resourceLinksState,
  };
  const visibleTabs = getVisibleTabs(tabVisibilityInput, resourceSource, isUpcomingGls);
  const activeTabId = visibleTabs.some((t) => t.id === tabId)
    ? tabId
    : (visibleTabs[0]?.id ?? tabId);

  const labelFor = (t: TabDef) => {
    const count = getTabItemCount(t.id, tabVisibilityInput);
    if (count != null) return `${t.label} (${count})`;
    return t.label;
  };

  // Reset to the requested initial tab whenever the dialog is opened. On
  // mobile we always start on the menu list regardless of `initialTab`.
  React.useEffect(() => {
    if (open) {
      setTabId(initialTab);
      setMobileSelected(null);
    }
  }, [open, initialTab]);

  const mobileActiveTab = mobileSelected
    ? visibleTabs.find((t) => t.id === mobileSelected) ?? null
    : null;
  const allTabsStillLoading = TAB_DEFS.every((t) => getTabItemCount(t.id, tabVisibilityInput) === null);
  const showGlobalEmpty = !allTabsStillLoading && visibleTabs.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          // Use the fully-opaque surface token. `--color-surface` is translucent
          // in the classic theme (rgba 0.85), which made the dialog look
          // see-through over the dashboard background.
          bgcolor: 'var(--color-surface-elevated-strong)',
          color: 'var(--color-text)',
          backgroundImage: 'none',
          border: { sm: '1px solid var(--color-border)' },
          borderRadius: { xs: 0, sm: 2 },
          // ~88% of the viewport on tablet/desktop, full screen on phones.
          width: { xs: '100%', sm: '88vw' },
          height: { xs: '100%', sm: '88vh' },
          maxWidth: { sm: '1480px' },
          maxHeight: { sm: '95vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {/* On mobile, show a back button when drilled into a tab */}
        {fullScreen && mobileActiveTab ? (
          <IconButton
            onClick={() => setMobileSelected(null)}
            aria-label="Back to resources menu"
            edge="start"
            sx={{ color: 'var(--color-text-strong)', mr: 0.5 }}
          >
            <ArrowBackIcon />
          </IconButton>
        ) : null}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 600,
              fontSize: '0.7rem',
              display: 'block',
            }}
          >
            {fullScreen && mobileActiveTab ? mobileActiveTab.label : 'Project Resources'}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'var(--color-text-strong)',
              lineHeight: 1.2,
              fontSize: { xs: '1rem', sm: '1.15rem' },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {projectName || projectId}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close project resources"
          sx={{ color: 'var(--color-text-strong)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Desktop: horizontal tabs. Mobile: hidden (replaced by a list menu). */}
      {!fullScreen && !showGlobalEmpty && visibleTabs.length > 0 && (
        <Box
          sx={{
            borderBottom: '1px solid var(--color-border)',
            bgcolor: 'var(--color-surface-elevated)',
          }}
        >
          <Tabs
            value={activeTabId}
            onChange={(_e, value: ProjectResourceTabId) => setTabId(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              px: { xs: 1, sm: 2 },
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.78rem', sm: '0.85rem' },
                color: 'var(--color-text-muted)',
                minHeight: 44,
                gap: 0.75,
                '&.Mui-selected': { color: 'var(--color-accent-strong)' },
              },
              '& .MuiTabs-indicator': { bgcolor: 'var(--color-accent)' },
            }}
          >
            {visibleTabs.map((t) => (
              <Tab
                key={t.id}
                value={t.id}
                icon={t.icon as React.ReactElement}
                iconPosition="start"
                label={labelFor(t)}
              />
            ))}
          </Tabs>
        </Box>
      )}

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 0 }}>
        {showGlobalEmpty ? (
          <AllResourcesEmptyState />
        ) : fullScreen ? (
          mobileActiveTab ? (
            // Drill-down: show only the selected section's content.
            <ResourceTabPanel
              key={mobileActiveTab.id}
              active
              tab={mobileActiveTab}
              projectId={projectId}
              filesState={filesState}
              saleskitState={saleskitState}
              ebooksState={ebooksState}
              edmsState={edmsState}
              mediaState={mediaState}
              seminarsState={seminarsState}
              resourceLinksState={resourceLinksState}
            />
          ) : (
            // Mobile menu list — tap a row to drill into that section.
            <Stack sx={{ py: { xs: 1.5, sm: 2 } }} spacing={1}>
              {visibleTabs.map((t) => (
                <ButtonBase
                  key={t.id}
                  onClick={() => setMobileSelected(t.id)}
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    px: 1.5,
                    py: 1.5,
                    borderRadius: 1.5,
                    border: '1.5px solid var(--color-border-strong)',
                    bgcolor: 'var(--color-surface)',
                    color: 'var(--color-text-strong)',
                    transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                    '&:hover, &:focus-visible': {
                      bgcolor: 'var(--color-accent-soft)',
                      borderColor: 'var(--color-accent)',
                      boxShadow: '0 0 0 1px var(--color-accent) inset',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      mr: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'var(--color-accent-soft)',
                      color: 'var(--color-accent-strong)',
                      flexShrink: 0,
                    }}
                  >
                    {t.icon}
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, flex: 1, fontSize: '0.95rem' }}
                  >
                    {labelFor(t)}
                  </Typography>
                  <ChevronRightIcon sx={{ color: 'var(--color-text-muted)' }} />
                </ButtonBase>
              ))}
            </Stack>
          )
        ) : (
          visibleTabs.map((t) => (
            <ResourceTabPanel
              key={t.id}
              active={t.id === activeTabId}
              tab={t}
              projectId={projectId}
              filesState={filesState}
              saleskitState={saleskitState}
              ebooksState={ebooksState}
              edmsState={edmsState}
              mediaState={mediaState}
              seminarsState={seminarsState}
              resourceLinksState={resourceLinksState}
            />
          ))
        )}
      </DialogContent>
    </Dialog>
  );
}
